/**
 * Send the user to another form.
 *
 * Use this — not `navigate('/m/<id>')` — whenever one page sends the user to a
 * different form ("Open Service Allocation" from a request, "List view" from
 * the schedule).
 *
 * WHY: the URL is not what moves the shell. `AppShell` chooses the rendered
 * form from its own `page` state, and its `open()` is what sets it — along with
 * closing the drawer, expanding the nav group so the sidebar highlights where
 * the user now is, and pushing the URL. A bare `navigate()` does only the last
 * of those, which leaves the address bar on the new form and the screen on the
 * old one.
 *
 * A window event rather than a prop, matching `ai:nav-collapse`: the pages that
 * need this are several components deep (a section inside a detail page inside
 * a list page), and threading a callback down that chain to every one of them
 * would be worse than the one listener AppShell already keeps.
 */
export function navigateToPage(pageId: string, recordId?: string): void {
  // Set BEFORE the event: AppShell handles it synchronously, so the target page
  // mounts inside this call and must already be able to read the handoff.
  pending = recordId ? { page: pageId, recordId } : null;
  window.dispatchEvent(new CustomEvent('ai:navigate', { detail: { page: pageId } }));
}

/**
 * The record a cross-page navigation asked the target form to OPEN, if any.
 *
 * Standing rule 10 says a form opens on its list — that governs clicking a
 * NAVIGATION entry, where no record has been chosen. Following a link from one
 * record to a related one ("Open request" on a service) is the "select a row"
 * step of List → select → detail, performed from somewhere else; landing on the
 * list and making the user find the row they just came from would lose the
 * thing they clicked.
 *
 * A module-level handoff rather than a prop or an event payload, for the reason
 * the whole file exists: AppShell renders every form from one switch, and
 * teaching it to pass a record id into ONE page would make the shell know that
 * page's internals. The target reads it on mount instead.
 *
 * CONSUMED, not read: it clears on the first call, so navigating to the same
 * form again later — from the sidebar, or by re-clicking the active item —
 * lands on the list as rule 10 requires, rather than reopening a stale record.
 */
export function consumePendingRecord(pageId: string): string | null {
  if (!pending || pending.page !== pageId) return null;
  const { recordId } = pending;
  pending = null;
  return recordId;
}

/**
 * Drop a handoff whose navigation did NOT happen.
 *
 * AppShell's open() can refuse the move — it asks "Unsaved changes will be
 * lost. Do you want to proceed?" first, and the user can say no. The record
 * was already parked by then, so without this it would survive and hijack the
 * NEXT visit to that form from the sidebar, opening a record the user did not
 * pick and breaking standing rule 10.
 */
export function clearPendingRecord(): void { pending = null; }

/** The pending handoff, if a navigation asked for one. Module-level because it
 *  must survive the unmount of the page that set it. */
let pending: { page: string; recordId: string } | null = null;
